import {useState} from 'react';

import {difference, isEmpty, splitEvery, union} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerMessageResponseBody,
  useMarkCommunicationsAsOpenedMutation,
  useMarkCommunicationsAsReadMutation,
  useMarkCommunicationsAsUnreadMutation,
} from '@dms/api/core';

import {ApiError} from 'shared/web';

import {MAX_COMMUNICATION_IDS_PER_REQUEST} from '../../../constants/MAX_COMMUNICATION_IDS_PER_REQUEST';
import {handleApiError} from '../../../utils/handleApiError';
import {ThreadReadState} from '../types/ThreadReadState';

const getUnreadIncomingIds = (messages: CustomerMessageResponseBody[]) =>
  messages
    .filter((message) => message.direction === 'INCOMING' && message.isUnread)
    .map((message) => message.id);

interface ThreadReadStateScope {
  customerId: string;
  resourceId: string;
  recordId: string;
}

/**
 * Read state of the thread for one visit. Every unread message the user sees is sent as opened
 * once; the backend reads it only when the user owns it. The ids stay marked as new for the visit.
 */
export function useThreadReadState(
  messages: CustomerMessageResponseBody[],
  scope: ThreadReadStateScope
) {
  // Lets the writes refetch only this thread and this record's count, not every list and count.
  const cacheScope = {
    ...(isNotNilOrEmpty(scope.customerId) ? {customerId: scope.customerId} : {}),
    resourceId: scope.resourceId,
    recordId: scope.recordId,
  };

  const [openedIds, setOpenedIds] = useState<string[]>([]);
  // Marked read or unread by hand: the user decided, so they are never sent as opened again.
  const [manuallyToggledIds, setManuallyToggledIds] = useState<string[]>([]);

  const [markCommunicationsAsOpened] = useMarkCommunicationsAsOpenedMutation();
  const [markCommunicationsAsRead] = useMarkCommunicationsAsReadMutation();
  const [markCommunicationsAsUnread] = useMarkCommunicationsAsUnreadMutation();

  const unreadIds = getUnreadIncomingIds(messages);
  const pendingOpenedIds = difference(unreadIds, union(openedIds, manuallyToggledIds));

  const openPendingMessages = () => {
    if (isEmpty(pendingOpenedIds)) {
      return;
    }

    setOpenedIds((ids) => union(ids, pendingOpenedIds));
    splitEvery(MAX_COMMUNICATION_IDS_PER_REQUEST, pendingOpenedIds).forEach((communicationIds) =>
      markCommunicationsAsOpened({communicationIds, ...cacheScope})
        .unwrap()
        // Silent because nobody asked for this write and reopening the record retries it.
        .catch((error: ApiError) => handleApiError(error, {silent: true}))
    );
  };

  const toggleManually = (
    message: CustomerMessageResponseBody,
    markCommunications: typeof markCommunicationsAsRead | typeof markCommunicationsAsUnread
  ) => {
    const wasToggledBefore = manuallyToggledIds.includes(message.id);
    setManuallyToggledIds((ids) => union(ids, [message.id]));
    markCommunications({communicationIds: [message.id], ...cacheScope})
      .unwrap()
      .catch((error: ApiError) => {
        // The state did not change. A first toggle is undone, so the message may be sent as opened
        // again; an earlier toggle that succeeded still stands.
        if (!wasToggledBefore) {
          setManuallyToggledIds((ids) => difference(ids, [message.id]));
        }
        handleApiError(error);
      });
  };

  const readState: ThreadReadState = {
    newMessageIds: union(unreadIds, openedIds),
    onMarkRead: (message) => toggleManually(message, markCommunicationsAsRead),
    onMarkUnread: (message) => toggleManually(message, markCommunicationsAsUnread),
  };

  return {readState, pendingOpenedIds, openPendingMessages};
}
